import { requestsSummaryActivity } from "@/lib/covi/summaryActivityPreference"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import {
  MAX_SECTION_ITEMS,
  MIN_COMPLETION_DELTA_POINTS,
  MIN_FINISHED_CONVERSATIONS_FOR_TREND,
  MIN_ISSUE_COUNT,
} from "./conversationsSummaryConstants"
import {
  formatMetadataValue,
  getAgentLabel,
  getMetricIds,
  metadataMatchesFocus,
  metricMatchesFocus,
} from "./conversationsSummaryFocus"
import {
  formatCount,
  formatDeltaPoints,
  formatMetricNumber,
  formatPercent,
  formatSentenceList,
  getPreviousTimeframeLabel,
  pluralize,
  summarizeCountEntries,
} from "./conversationsSummaryFormat"
import {
  getMetricIssueCounts,
  getMetricLabel,
  getNumericMetricMovements,
  getOutcomeMetricMovements,
  getStringMetricSignals,
  incrementCount,
  sortCountEntries,
  type MetricMovement,
  type StringMetricSignal,
} from "./conversationsSummaryMetrics"
import { getFailureStatusCounts } from "./conversationsSummaryStatus"
import type {
  ConversationsPageSummaryLookups,
  ConversationsSummaryFocus,
  ConversationsSummaryWindowStats,
  CountEntry,
  ResolvedConversationsSummaryTimeframe,
} from "./conversationsSummaryTypes"

export {
  buildFocusItems,
  getLeadSentence,
} from "./conversationsSummaryFocusNarrative"
export {
  getMetricIssueCounts,
  getNumericMetricMovements,
  getOutcomeMetricMovements,
  getStringMetricSignals,
}
export { parseConversationsSummaryFocusText } from "./conversationsSummaryFocus"

function getFirstSeenEntries(
  currentRows: ConversationsTableResult[],
  previousRows: ConversationsTableResult[],
  dimension: "agent" | "metric" | "tag" | "metadata",
  lookups: ConversationsPageSummaryLookups | undefined,
  focus: ConversationsSummaryFocus,
): CountEntry[] {
  const previousIds = new Set<string>()
  const counts = new Map<string, CountEntry>()

  const addDimension = (
    row: ConversationsTableResult,
    target: Set<string> | Map<string, CountEntry>,
  ) => {
    const add = (id: string, label = id) => {
      if (target instanceof Set) {
        target.add(id)
      } else {
        incrementCount(target, id, label)
      }
    }

    if (dimension === "agent") {
      const id = row.agent_id ?? "unknown-agent"
      add(id, getAgentLabel(row.agent_id, lookups))
      return
    }

    if (dimension === "metric") {
      getMetricIds(row).forEach((metricId) =>
        add(metricId, getMetricLabel(metricId, lookups)),
      )
      return
    }

    if (dimension === "tag") {
      ;(row.tags ?? []).forEach((tag) => add(tag))
      return
    }

    Object.entries(row.customer_metadata ?? {}).forEach(([key, value]) => {
      const formattedValue = formatMetadataValue(value)
      if (formattedValue && metadataMatchesFocus(key, formattedValue, focus)) {
        add(`${key}:${formattedValue}`, `${key}: ${formattedValue}`)
      }
    })
  }

  previousRows.forEach((row) => addDimension(row, previousIds))
  currentRows.forEach((row) => {
    const rowCounts = new Map<string, CountEntry>()
    addDimension(row, rowCounts)
    rowCounts.forEach((entry) => {
      if (!previousIds.has(entry.id)) {
        incrementCount(counts, entry.id, entry.label)
      }
    })
  })

  return sortCountEntries(counts)
}

function getCompletionTrendSentence(
  currentStats: ConversationsSummaryWindowStats,
  previousStats: ConversationsSummaryWindowStats,
  timeframe: ResolvedConversationsSummaryTimeframe,
): string | null {
  if (
    currentStats.finishedConversations < MIN_FINISHED_CONVERSATIONS_FOR_TREND ||
    previousStats.finishedConversations <
      MIN_FINISHED_CONVERSATIONS_FOR_TREND ||
    currentStats.completionRate === null ||
    previousStats.completionRate === null
  ) {
    return null
  }

  const delta = currentStats.completionRate - previousStats.completionRate
  if (Math.abs(delta * 100) < MIN_COMPLETION_DELTA_POINTS) {
    return `Completion is steady vs the ${getPreviousTimeframeLabel(timeframe)} at ${formatPercent(
      currentStats.completionRate,
    )}.`
  }

  return `Completion moved ${formatDeltaPoints(delta)} ${delta > 0 ? "up" : "down"} vs the ${getPreviousTimeframeLabel(
    timeframe,
  )}, from ${formatPercent(previousStats.completionRate)} to ${formatPercent(
    currentStats.completionRate,
  )}.`
}

function buildStatusSentence(
  currentStats: ConversationsSummaryWindowStats,
): string {
  if (currentStats.statusKnownConversations === 0) {
    return "Status detail was not available for these rows, so the summary is leaning on metric outputs and row context."
  }

  const statusParts = [
    currentStats.completedConversations > 0
      ? `${formatCount(currentStats.completedConversations)} completed`
      : null,
    currentStats.unsuccessfulConversations > 0
      ? `${formatCount(currentStats.unsuccessfulConversations)} unsuccessful`
      : null,
    currentStats.inProgressConversations > 0
      ? `${formatCount(currentStats.inProgressConversations)} in progress`
      : null,
    currentStats.otherStatusConversations > 0
      ? `${formatCount(
          currentStats.otherStatusConversations,
        )} other known ${pluralize(
          currentStats.otherStatusConversations,
          "status",
          "statuses",
        )}`
      : null,
  ].filter((part): part is string => part !== null)

  return `${formatCount(
    currentStats.statusKnownConversations,
  )} had status detail: ${formatSentenceList(statusParts)}.`
}

export function buildSummaryText({
  currentStats,
  leadSentence,
  previousStats,
  resolvedTimeframe,
  timeframeLabel,
  includeActivity = false,
}: {
  currentStats: ConversationsSummaryWindowStats
  leadSentence: string | null
  previousStats: ConversationsSummaryWindowStats
  resolvedTimeframe: ResolvedConversationsSummaryTimeframe
  timeframeLabel: string
  includeActivity?: boolean
}): string {
  if (currentStats.totalConversations === 0) {
    return `No conversations are available for ${timeframeLabel.toLowerCase()} with the current filters.`
  }

  if (!includeActivity) {
    return (
      leadSentence ??
      `Agent performance cannot be assessed for ${timeframeLabel.toLowerCase()}: no supported metric outcome or meaningful change is available.`
    )
  }

  const statusSentence = buildStatusSentence(currentStats)
  const trendSentence = getCompletionTrendSentence(
    currentStats,
    previousStats,
    resolvedTimeframe,
  )
  const pieces = [
    `In ${timeframeLabel.toLowerCase()}, ${formatCount(
      currentStats.totalConversations,
    )} ${pluralize(
      currentStats.totalConversations,
      "conversation",
    )} matched the current filters.`,
    statusSentence,
  ]

  if (trendSentence) {
    pieces.push(trendSentence)
  }
  if (leadSentence) {
    pieces.push(leadSentence)
  }

  return pieces.join(" ")
}

export function buildWhatChangedItems({
  currentRows,
  focus,
  metricMovements,
  previousRows,
  lookups,
}: {
  currentRows: ConversationsTableResult[]
  focus: ConversationsSummaryFocus
  metricMovements: MetricMovement[]
  previousRows: ConversationsTableResult[]
  lookups: ConversationsPageSummaryLookups | undefined
}): string[] {
  const items: string[] = []
  const firstSeenAgents = getFirstSeenEntries(
    currentRows,
    previousRows,
    "agent",
    lookups,
    focus,
  )
  const firstSeenMetrics = getFirstSeenEntries(
    currentRows,
    previousRows,
    "metric",
    lookups,
    focus,
  )
  const firstSeenTags = getFirstSeenEntries(
    currentRows,
    previousRows,
    "tag",
    lookups,
    focus,
  )
  const firstSeenMetadata = getFirstSeenEntries(
    currentRows,
    previousRows,
    "metadata",
    lookups,
    focus,
  )

  const includeActivity = requestsSummaryActivity(focus.rawText)
  if (includeActivity && firstSeenAgents.length > 0) {
    items.push(
      `Agents first seen in this window: ${summarizeCountEntries(firstSeenAgents)}.`,
    )
  }
  if (includeActivity && firstSeenMetrics.length > 0) {
    items.push(
      `Metrics first seen in this window: ${summarizeCountEntries(firstSeenMetrics)}.`,
    )
  }
  if (includeActivity && firstSeenTags.length > 0) {
    items.push(
      `Tags first seen in this window: ${summarizeCountEntries(firstSeenTags)}.`,
    )
  }
  if (firstSeenMetadata.length > 0) {
    items.push(
      `Focused metadata first seen in this window: ${summarizeCountEntries(firstSeenMetadata)}.`,
    )
  }

  const rankedMetricMovements = [...metricMovements].sort((a, b) => {
    const aFocused = metricMatchesFocus(a.id, lookups, focus)
    const bFocused = metricMatchesFocus(b.id, lookups, focus)
    return Number(bFocused) - Number(aFocused)
  })

  rankedMetricMovements.slice(0, MAX_SECTION_ITEMS).forEach((movement) => {
    items.push(
      `${movement.label} averaged ${formatMetricNumber(
        movement.currentAverage,
      )}, vs ${formatMetricNumber(
        movement.previousAverage,
      )} in the prior comparable window.`,
    )
  })

  return items.slice(0, MAX_SECTION_ITEMS)
}

export function buildWorthDiggingItems({
  currentRows,
  focus,
  metricIssues,
  stringMetricSignals,
  lookups,
}: {
  currentRows: ConversationsTableResult[]
  focus: ConversationsSummaryFocus
  metricIssues: CountEntry[]
  stringMetricSignals: StringMetricSignal[]
  lookups: ConversationsPageSummaryLookups | undefined
}): string[] {
  const items: string[] = []
  const failureStatuses = getFailureStatusCounts(currentRows)

  if (requestsSummaryActivity(focus.rawText) && failureStatuses.length > 0) {
    items.push(
      `Conversation failures/status issues: ${summarizeCountEntries(failureStatuses)}.`,
    )
  }

  const rankedMetricIssues = [...metricIssues].sort((a, b) => {
    const aFocused = metricMatchesFocus(
      a.id.split(":")[0] ?? a.id,
      lookups,
      focus,
    )
    const bFocused = metricMatchesFocus(
      b.id.split(":")[0] ?? b.id,
      lookups,
      focus,
    )
    return Number(bFocused) - Number(aFocused)
  })

  rankedMetricIssues.slice(0, MAX_SECTION_ITEMS).forEach((entry) => {
    if (entry.count >= MIN_ISSUE_COUNT) {
      items.push(
        `${entry.label} appeared on ${formatCount(entry.count)} ${pluralize(
          entry.count,
          "metric output",
        )}.`,
      )
    }
  })

  stringMetricSignals.slice(0, MAX_SECTION_ITEMS).forEach((signal) => {
    items.push(
      `${signal.label} returned ${signal.topNegativeValue} on ${formatCount(
        signal.negativeCount,
      )} of ${formatCount(signal.totalCount)} evaluated conversations.`,
    )
  })

  return items.slice(0, MAX_SECTION_ITEMS)
}
